import type {AxiosResponse} from "axios";
import type {IvrConfigCreateElementPayload, IvrConfigSchedulerUpdatePayload,} from "../../../../redux/api/apiIVRConfiguration";
import {
    buildIvrElementDraftDataString,
    mergeDraftDataWithPreviousForPayload,
    normalizeIvrSchedulerScheduleKind,
    parseIvrElementDraftData,
} from "./ivrElementDraftData";
import {IVR_END_ELEMENT_RESERVED_ID} from "./ivrSchemeConstants";
import {blockConfigToApiElement, normalizeIvrScheduleApiTime,} from "./ivrSchemeSerialization";
import {type IvrApiSchedule, ivrRawScheduleElementId, ivrRawScheduleIsActive, ivrRawScheduleRowId, type IvrSchedulerSlotRow, schedulerDayOfWeekToDaysMask,} from "./ivrSchemeHydration";

type ExistingApiElement = {
    elementId?: number | null;
    elementType?: string | null;
};

type DraftSchedule = {
    elementId?: number | null;
    daysMask?: number | null;
};

export type CreateElementPayloadOptions = {
    existingApiElements?: readonly ExistingApiElement[];
    graphElementIds?: readonly number[];
    graphEndElementIds?: readonly number[];
    entryElementId?: number | null;
    previousElementDraftData?: unknown;
    overrideDraftData?: string | null;
    ivrSchedulesForDraft?: readonly DraftSchedule[];
    updateSchedulers?: readonly IvrConfigSchedulerUpdatePayload[];
};

export type WeekdaySchedulerDayState = {
    dayOfWeek: number;
    enabled: boolean;
    startHHMM: string;
    endHHMM: string;
};

function daysMaskIsSingleWeekday(mask: number): boolean {
    const m = Number(mask) || 0;
    return m > 0 && (m & (m - 1)) === 0;
}

function desiredDaysMaskFromWeekdayStates(
    states: readonly WeekdaySchedulerDayState[]
): number {
    let acc = 0;
    for (const d of states) {
        if (!d.enabled) {
            continue;
        }
        if (
            !String(d.startHHMM ?? "").trim() ||
            !String(d.endHHMM ?? "").trim()
        ) {
            continue;
        }
        acc |= schedulerDayOfWeekToDaysMask(d.dayOfWeek);
    }
    return acc;
}

function ivrScheduleRowRecord(s: IvrApiSchedule): Record<string, unknown> {
    return s as unknown as Record<string, unknown>;
}

function ivrRawScheduleDaysMaskFromRow(s: IvrApiSchedule): number {
    const r = ivrScheduleRowRecord(s);
    const v = r.daysMask ?? r.DaysMask;
    const n = typeof v === "number" ? v : Number(String(v ?? "").trim());
    return Number.isFinite(n) ? n : 0;
}

function ivrRawScheduleFromToStrings(s: IvrApiSchedule): {
    from: string;
    to: string;
} {
    const r = ivrScheduleRowRecord(s);
    return {
        from: String(r.fromTime ?? r.FromTime ?? ""),
        to: String(r.toTime ?? r.ToTime ?? ""),
    };
}

function ivrRawScheduleIsActiveFromRow(s: IvrApiSchedule): boolean | undefined {
    const r = ivrScheduleRowRecord(s);
    const v = r.isActive ?? r.IsActive;
    if (v === false) return false;
    if (v === true) return true;
    return undefined;
}

export function buildIvrSchedulerUpdateSchedulersPayload(
    elementId: number,
    apiSchedules: readonly IvrApiSchedule[] | undefined,
    weekdayStates: readonly WeekdaySchedulerDayState[],
    persistedSlotRows?: readonly Pick<
        IvrSchedulerSlotRow,
        "id" | "daysMask" | "start" | "end"
    >[]
): IvrConfigSchedulerUpdatePayload[] {
    const list = apiSchedules ?? [];
    const updates: IvrConfigSchedulerUpdatePayload[] = [];

    for (const s of list) {
        if (ivrRawScheduleElementId(s) !== elementId) {
            continue;
        }

        const id = ivrRawScheduleRowId(s);
        if (id <= 0) {
            continue;
        }

        const daysMask = ivrRawScheduleDaysMaskFromRow(s);
        if (!daysMaskIsSingleWeekday(daysMask)) {
            continue;
        }

        const ft = ivrRawScheduleFromToStrings(s);
        const fromApi = normalizeIvrScheduleApiTime(ft.from);
        const toApi = normalizeIvrScheduleApiTime(ft.to);
        const wasInactive = ivrRawScheduleIsActiveFromRow(s) === false;

        const enabledDay = weekdayStates.find(
            (d) =>
                d.enabled &&
                schedulerDayOfWeekToDaysMask(d.dayOfWeek) === daysMask &&
                String(d.startHHMM ?? "").trim() !== "" &&
                String(d.endHHMM ?? "").trim() !== ""
        );

        if (!enabledDay) {
            if (!wasInactive) {
                updates.push({
                    id,
                    daysMask,
                    fromTime: fromApi,
                    toTime: toApi,
                    isActive: false,
                });
            }
            continue;
        }

        const nf = normalizeIvrScheduleApiTime(enabledDay.startHHMM);
        const nt = normalizeIvrScheduleApiTime(enabledDay.endHHMM);
        if (nf !== fromApi || nt !== toApi || wasInactive) {
            updates.push({
                id,
                daysMask,
                fromTime: nf,
                toTime: nt,
                isActive: true,
            });
        }
    }

    const desiredUnion = desiredDaysMaskFromWeekdayStates(weekdayStates);
    const updateIds = new Set(updates.map((u) => u.id));

    for (const s of list) {
        if (ivrRawScheduleElementId(s) !== elementId) {
            continue;
        }
        const id = ivrRawScheduleRowId(s);
        if (id <= 0 || updateIds.has(id)) {
            continue;
        }
        const m = ivrRawScheduleDaysMaskFromRow(s);
        if (m === 0) {
            continue;
        }
        if (daysMaskIsSingleWeekday(m)) {
            continue;
        }
        if ((m & desiredUnion) === m) {
            continue;
        }
        if (ivrRawScheduleIsActiveFromRow(s) === false) {
            continue;
        }
        const ft = ivrRawScheduleFromToStrings(s);
        updates.push({
            id,
            daysMask: m,
            fromTime: normalizeIvrScheduleApiTime(ft.from),
            toTime: normalizeIvrScheduleApiTime(ft.to),
            isActive: false,
        });
    }

    const handledIds = new Set(updates.map((u) => u.id));

    for (const row of persistedSlotRows ?? []) {
        const ridRaw = row.id;
        const rid =
            typeof ridRaw === "number" ? ridRaw : Number(String(ridRaw ?? "").trim());
        if (!Number.isFinite(rid) || rid <= 0 || handledIds.has(rid)) {
            continue;
        }
        const m = Number(row.daysMask) || 0;
        if (!daysMaskIsSingleWeekday(m)) {
            continue;
        }

        const fromRow = normalizeIvrScheduleApiTime(row.start);
        const toRow = normalizeIvrScheduleApiTime(row.end);

        const enabledDay = weekdayStates.find(
            (d) =>
                d.enabled &&
                schedulerDayOfWeekToDaysMask(d.dayOfWeek) === m &&
                String(d.startHHMM ?? "").trim() !== "" &&
                String(d.endHHMM ?? "").trim() !== ""
        );

        if (!enabledDay) {
            updates.push({
                id: rid,
                daysMask: m,
                fromTime: fromRow,
                toTime: toRow,
                isActive: false,
            });
            handledIds.add(rid);
            continue;
        }

        const matchingApiForRow = list.find(
            (s) => ivrRawScheduleRowId(s) === rid
        );
        const needsReactivateFromPersisted =
            matchingApiForRow != null &&
            ivrRawScheduleIsActive(matchingApiForRow) === false;

        const nf = normalizeIvrScheduleApiTime(enabledDay.startHHMM);
        const nt = normalizeIvrScheduleApiTime(enabledDay.endHHMM);
        if (nf !== fromRow || nt !== toRow || needsReactivateFromPersisted) {
            updates.push({
                id: rid,
                daysMask: m,
                fromTime: nf,
                toTime: nt,
                isActive: true,
            });
            handledIds.add(rid);
        }
    }

    return updates;
}

const SCHEDULER_ALL_DAYS_MASK = 127;

function onceAdvancedSlotTimeMaskKey(
    row: Pick<IvrSchedulerSlotRow, "daysMask" | "start" | "end">
): string {
    const mask = Number(row.daysMask) || 0;
    const s = normalizeIvrScheduleApiTime(String(row.start ?? ""));
    const e = normalizeIvrScheduleApiTime(String(row.end ?? ""));
    return `${mask}|${s}|${e}`;
}

function onceAdvancedApiScheduleTimeMaskKey(s: IvrApiSchedule): string {
    const mask = ivrRawScheduleDaysMaskFromRow(s);
    const ft = ivrRawScheduleFromToStrings(s);
    return `${mask}|${normalizeIvrScheduleApiTime(ft.from)}|${normalizeIvrScheduleApiTime(ft.to)}`;
}

export function buildIvrSchedulerDeactivateAllForElementPayload(
    elementId: number,
    apiSchedules: readonly IvrApiSchedule[] | undefined
): IvrConfigSchedulerUpdatePayload[] {
    const updates: IvrConfigSchedulerUpdatePayload[] = [];
    for (const s of apiSchedules ?? []) {
        if (ivrRawScheduleElementId(s) !== elementId) {
            continue;
        }
        const id = ivrRawScheduleRowId(s);
        if (id <= 0) {
            continue;
        }
        if (ivrRawScheduleIsActiveFromRow(s) === false) {
            continue;
        }
        const daysMask = ivrRawScheduleDaysMaskFromRow(s);
        const ft = ivrRawScheduleFromToStrings(s);
        updates.push({
            id,
            daysMask,
            fromTime: normalizeIvrScheduleApiTime(ft.from),
            toTime: normalizeIvrScheduleApiTime(ft.to),
            isActive: false,
        });
    }
    return updates;
}

export function buildIvrOnceAdvancedUpdateSchedulersPayload(
    elementId: number,
    apiSchedules: readonly IvrApiSchedule[] | undefined,
    savedSlotRows: readonly Pick<
        IvrSchedulerSlotRow,
        "id" | "daysMask" | "start" | "end"
    >[]
): IvrConfigSchedulerUpdatePayload[] {
    const list = apiSchedules ?? [];
    const updates: IvrConfigSchedulerUpdatePayload[] = [];
    const saved127 = savedSlotRows.filter(
        (r) => (Number(r.daysMask) || 0) === SCHEDULER_ALL_DAYS_MASK
    );
    const savedById = new Map<number, (typeof saved127)[number]>();
    const savedByTimeKey = new Map<string, (typeof saved127)[number]>();
    for (const row of saved127) {
        const idRaw = row.id;
        const idNum =
            typeof idRaw === "number" ? idRaw : Number(idRaw ?? 0);
        if (Number.isFinite(idNum) && idNum > 0) {
            savedById.set(idNum, row);
        }
        savedByTimeKey.set(onceAdvancedSlotTimeMaskKey(row), row);
    }
    const handledIds = new Set<number>();

    for (const s of list) {
        if (ivrRawScheduleElementId(s) !== elementId) {
            continue;
        }
        const id = ivrRawScheduleRowId(s);
        if (id <= 0) {
            continue;
        }
        const daysMask = ivrRawScheduleDaysMaskFromRow(s);
        if (daysMask !== SCHEDULER_ALL_DAYS_MASK) {
            continue;
        }

        const ft = ivrRawScheduleFromToStrings(s);
        const fromApi = normalizeIvrScheduleApiTime(ft.from);
        const toApi = normalizeIvrScheduleApiTime(ft.to);
        const wasInactive = ivrRawScheduleIsActiveFromRow(s) === false;

        const savedMatch =
            savedById.get(id) ??
            savedByTimeKey.get(onceAdvancedApiScheduleTimeMaskKey(s));

        if (!savedMatch) {
            if (!wasInactive) {
                updates.push({
                    id,
                    daysMask,
                    fromTime: fromApi,
                    toTime: toApi,
                    isActive: false,
                });
            }
            handledIds.add(id);
            continue;
        }

        const nf = normalizeIvrScheduleApiTime(String(savedMatch.start ?? ""));
        const nt = normalizeIvrScheduleApiTime(String(savedMatch.end ?? ""));
        if (nf !== fromApi || nt !== toApi || wasInactive) {
            updates.push({
                id,
                daysMask: SCHEDULER_ALL_DAYS_MASK,
                fromTime: nf,
                toTime: nt,
                isActive: true,
            });
        }
        handledIds.add(id);
    }

    for (const row of saved127) {
        const ridRaw = row.id;
        const rid =
            typeof ridRaw === "number"
                ? ridRaw
                : Number(String(ridRaw ?? "").trim());
        if (!Number.isFinite(rid) || rid <= 0 || handledIds.has(rid)) {
            continue;
        }
        const matchingApiForRow = list.find(
            (s) => ivrRawScheduleRowId(s) === rid
        );
        const needsReactivateFromPersisted =
            matchingApiForRow != null &&
            ivrRawScheduleIsActiveFromRow(matchingApiForRow) === false;
        const nf = normalizeIvrScheduleApiTime(String(row.start ?? ""));
        const nt = normalizeIvrScheduleApiTime(String(row.end ?? ""));

        if (matchingApiForRow) {
            const ft = ivrRawScheduleFromToStrings(matchingApiForRow);
            const fromApi = normalizeIvrScheduleApiTime(ft.from);
            const toApi = normalizeIvrScheduleApiTime(ft.to);
            if (nf !== fromApi || nt !== toApi || needsReactivateFromPersisted) {
                updates.push({
                    id: rid,
                    daysMask: SCHEDULER_ALL_DAYS_MASK,
                    fromTime: nf,
                    toTime: nt,
                    isActive: true,
                });
            }
        } else if (needsReactivateFromPersisted) {
            updates.push({
                id: rid,
                daysMask: SCHEDULER_ALL_DAYS_MASK,
                fromTime: nf,
                toTime: nt,
                isActive: true,
            });
        }
    }

    return updates;
}

function parseNumber(value: unknown): number | null {
    if (value == null || value === "") {
        return null;
    }

    const parsed =
        typeof value === "number"
            ? value
            : Number(String(value).trim());

    return Number.isFinite(parsed) ? parsed : null;
}

function parsePositiveNumber(value: unknown): number | null {
    const parsed = parseNumber(value);
    return parsed != null && parsed > 0 ? parsed : null;
}

function parseNonNegativeNumber(value: unknown): number | null {
    const parsed = parseNumber(value);
    return parsed != null && parsed >= 0 ? parsed : null;
}

function normalizeElementType(value: unknown): string {
    return String(value ?? "").toUpperCase();
}

function toValidElementId(value: unknown): number | null {
    const parsed = parsePositiveNumber(value);

    if (parsed == null || parsed === IVR_END_ELEMENT_RESERVED_ID) {
        return null;
    }

    return parsed;
}

function buildDraftData(
    blockConfig: Record<string, unknown>,
    elementType: string,
    elementId: number,
    options?: CreateElementPayloadOptions
): string | undefined {
    const trimmedOverride =
        options?.overrideDraftData != null
            ? String(options.overrideDraftData).trim()
            : "";
    if (trimmedOverride !== "") {
        const et = String(elementType ?? "").toUpperCase();
        const isSchedulerLike = et === "SCHEDULER" || et === "SCHEDULE";
        if (isSchedulerLike) {
            const parsed = parseIvrElementDraftData(trimmedOverride);
            const kind = normalizeIvrSchedulerScheduleKind(
                parsed?.schedulerScheduleKind
            );
            const days = parsed?.schedulerDays;
            if (
                parsed &&
                Array.isArray(days) &&
                days.length === 0 &&
                kind !== "once" &&
                kind !== "once_advanced" &&
                kind !== "daysweek_advanced"
            ) {
            } else {
                return trimmedOverride;
            }
        } else {
            return trimmedOverride;
        }
    }

    const draftRaw = buildIvrElementDraftDataString(blockConfig, elementType, {
        elementId,
        schedules: options?.ivrSchedulesForDraft,
    });

    return mergeDraftDataWithPreviousForPayload(
        draftRaw,
        elementType,
        options?.previousElementDraftData
    );
}

function resolveElementIdForPayload(
    element: ReturnType<typeof blockConfigToApiElement>,
    blockConfig: Record<string, unknown>,
    options?: CreateElementPayloadOptions
): number {
    const isNew = isNewIvrConfiguratorElement(blockConfig);
    const isEnd = element.elementType === "END";

    if (isEnd && isNew) {
        return computeNextIvrEndElementId(
            options?.existingApiElements ?? [],
            options?.graphEndElementIds ?? []
        );
    }

    let elementIdForPayload = parseNonNegativeNumber(element.elementId) ?? 0;

    if (!isEnd && isNew && elementIdForPayload <= 0) {
        const existingNonEndElements = (options?.existingApiElements ?? []).filter(
            (item) => normalizeElementType(item.elementType) !== "END"
        );

        if (
            existingNonEndElements.length === 0 &&
            options?.entryElementId != null
        ) {
            return Number(options.entryElementId);
        }

        return computeNextIvrElementId(
            options?.existingApiElements ?? [],
            options?.graphElementIds ?? []
        );
    }

    return elementIdForPayload;
}

function slimAudiosForElementPayload(
    audios: { audioId: number; audioName?: string | null }[] | null | undefined
): { audioId: number; audioName: string }[] | null {
    if (!audios?.length) {
        return null;
    }

    return audios.map((audio) => ({
        audioId: audio.audioId,
        audioName: audio.audioName != null ? String(audio.audioName) : "",
    }));
}

function resolvePayloadDisplayName(
    elementType: string,
    apiName: string | null | undefined
): string | null {
    const normalizedName = String(apiName ?? "").trim();

    if (normalizedName) {
        return normalizedName;
    }

    if (
        elementType === "PLAY" ||
        elementType === "MENU" ||
        elementType === "SCHEDULER" ||
        elementType === "OPERATOR"
    ) {
        return "";
    }

    return apiName ?? null;
}

export function computeNextIvrElementId(
    existingApiElements: readonly ExistingApiElement[],
    graphElementIds: readonly number[] = []
): number {
    const usedIds = new Set<number>();

    for (const element of existingApiElements) {
        if (normalizeElementType(element.elementType) === "END") {
            continue;
        }
        const elementId = toValidElementId(element.elementId);

        if (elementId != null) {
            usedIds.add(elementId);
        }
    }

    for (const graphElementId of graphElementIds) {
        const elementId = toValidElementId(graphElementId);

        if (elementId != null) {
            usedIds.add(elementId);
        }
    }

    let maxId = 0;
    usedIds.forEach((id) => {
        if (id > maxId) maxId = id;
    });

    return maxId + 1;
}

export function computeNextIvrEndElementId(
    existingApiElements: readonly ExistingApiElement[],
    graphEndElementIds: readonly number[] = []
): number {
    let maxEnd = IVR_END_ELEMENT_RESERVED_ID - 1;

    for (const element of existingApiElements) {
        if (normalizeElementType(element.elementType) !== "END") {
            continue;
        }
        const id = parsePositiveNumber(element.elementId);
        if (id != null) {
            maxEnd = Math.max(maxEnd, id);
        }
    }

    for (const raw of graphEndElementIds) {
        const id = parsePositiveNumber(raw);
        if (id != null) {
            maxEnd = Math.max(maxEnd, id);
        }
    }

    return maxEnd + 1;
}

export function isNewIvrConfiguratorElement(blockConfig: {
    elementId?: unknown;
}): boolean {
    const elementId = parseNumber(blockConfig.elementId);

    if (elementId == null) {
        return true;
    }

    return elementId < 0;
}

export function shouldPostNewIvrConfiguratorElement(blockConfig: {
    elementId?: unknown;
    startConfiguration?: unknown;
    elementType?: unknown;
}): boolean {
    return isNewIvrConfiguratorElement(blockConfig);
}

export function blockConfigToCreateElementPayload(
    flowId: number,
    blockConfig: Record<string, unknown>,
    options?: CreateElementPayloadOptions
): IvrConfigCreateElementPayload {
    const apiElement = blockConfigToApiElement(blockConfig, flowId);
    const fromElementId = parseNonNegativeNumber(blockConfig.fromElementId);
    const elementIdForPayload = resolveElementIdForPayload(
        apiElement,
        blockConfig,
        options
    );
    const displayName = resolvePayloadDisplayName(
        apiElement.elementType,
        apiElement.name
    );

    if (apiElement.elementType === "END") {
        return {
            flowId,
            elementId: elementIdForPayload,
            name: displayName ?? "END",
            elementType: "END",
        };
    }

    const draftData = buildDraftData(
        blockConfig,
        apiElement.elementType,
        elementIdForPayload,
        options
    );

    if (apiElement.elementType === "SCHEDULER") {
        const payload: IvrConfigCreateElementPayload = {
            flowId,
            elementId: elementIdForPayload,
            name: displayName,
            elementType: "SCHEDULER",
            ...(draftData != null ? { draftData } : {}),
            ...(options?.updateSchedulers?.length
                ? { updateSchedulers: [...options.updateSchedulers] }
                : {}),
        };

        if (fromElementId != null) {
            payload.fromElementId = fromElementId;
        }

        return payload;
    }

    return {
        flowId,
        elementId: elementIdForPayload,
        ...(fromElementId != null ? { fromElementId } : {}),
        name: displayName,
        elementType: apiElement.elementType,
        audios: slimAudiosForElementPayload(apiElement.audios ?? null),
        elementData: apiElement.elementData ?? null,
        ...(draftData != null ? { draftData } : {}),
        minDigits: apiElement.minDigits ?? null,
        maxDigits: apiElement.maxDigits ?? null,
        terminators: apiElement.terminators ?? null,
        regexp: apiElement.regexp ?? null,
        digitTimeOut: apiElement.digitTimeOut ?? null,
        timeOut: apiElement.timeOut ?? null,
        silencePre: apiElement.silencePre ?? null,
        silencePost: apiElement.silencePost ?? null,
    };
}

export function extractCreatedElementIdFromResponse(
    response: AxiosResponse<unknown>
): number | null {
    const responseData = response.data;

    if (
        responseData == null ||
        typeof responseData !== "object" ||
        Array.isArray(responseData)
    ) {
        return null;
    }

    const raw = responseData as Record<string, unknown>;
    const payload =
        raw.data != null &&
        typeof raw.data === "object" &&
        !Array.isArray(raw.data)
            ? (raw.data as Record<string, unknown>)
            : raw;

    return parsePositiveNumber(
        payload.elementId ?? payload.id ?? payload.ElementId
    );
}
